import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { CHIP_SHAPE, cn, FOCUS_RING } from '@/components/base'
import { useChipDraft } from '@/hooks/use-chip-draft'

/**
 * The band a row of filter controls sits in.
 *
 * Two of these stack on the documents screen — the project's required slots
 * over the table's own filters — so the inset is shared: a jog in the left
 * edge between them is visible at a glance.
 *
 * Wraps, because the same row sits beside a pane whose divider the operator
 * drags, and the tally has to fall under the filters rather than out of it.
 */
export const FILTER_ROW_CLASS =
  'flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 ' +
  'border-b px-3.5 py-2.5'

/** The lane the chips themselves run down, inside that band. */
export const FILTER_ROW_LANE_CLASS =
  'flex flex-1 flex-row flex-wrap items-center gap-2'

/**
 * The shape of every filter control in Internal: `CHIP_SHAPE`'s box, in the
 * quiet ink a row of them is read in. Anything that sits in such a row wears
 * this.
 */
export const FILTER_CHIP_CLASS =
  `flex ${CHIP_SHAPE} border border-border text-muted-foreground ` +
  `transition-colors hover:border-border ${FOCUS_RING}`

/**
 * How wide a filter control may get.
 *
 * The chip sizes to what it says and the search box takes the same ceiling, so
 * one long customer name cannot leave the two halves of the row at different
 * widths.
 */
export const FILTER_CONTROL_WIDTH = 'max-w-56'

/** The same chip, pressed. */
export const FILTER_CHIP_ACTIVE =
  'border-primary bg-primary text-primary-foreground hover:border-primary'

/**
 * One filter, as a toggle.
 *
 * `count` is what makes a chip worth more than a dropdown entry — the reader
 * picks the bucket by seeing how full it is.
 */
export function FilterChip({
  label,
  count,
  icon: Icon,
  active,
  onClick,
}: {
  label: string
  count?: number
  icon?: LucideIcon
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(FILTER_CHIP_CLASS, active && FILTER_CHIP_ACTIVE)}
    >
      {Icon ? <Icon size={12} aria-hidden /> : null}
      {count !== undefined ? (
        <span className="font-semibold tabular-nums">{count}</span>
      ) : null}
      <span className="font-medium">{label}</span>
    </button>
  )
}

/**
 * The same chip, typed into.
 *
 * One shell for every filter that takes text rather than a press: the search
 * box of a toolbar, the bound of a numeric range. `useChipDraft` holds what is
 * typed and reports it on a beat, so a filtered refetch does not fire per
 * character.
 *
 * A `label`, so the padding and whatever sits before the field focus it too —
 * the chip is the size of the target the reader aims at.
 */
export function FilterChipField({
  leading,
  value,
  onChange,
  type,
  width,
  active,
  delayMs,
  placeholder,
}: {
  /** What sits before the field — a magnifier, the bound's name. */
  leading?: ReactNode
  value: string
  onChange: (value: string) => void
  type: 'search' | 'number'
  /** The chip's width class. `w-full` where it fills a column. */
  width?: string
  /** Draws it as a filter in force — for a toolbar that says what is narrowed. */
  active?: boolean
  /** What typing pauses before the parent hears it. `0` reports every
   *  keystroke, for a list filtered in the browser. */
  delayMs?: number
  placeholder?: string
}) {
  const draft = useChipDraft(value, onChange, delayMs)

  return (
    <label
      className={cn(
        FILTER_CHIP_CLASS,
        // `shrink-0` because the chip's height is a height and not a
        // suggestion: in a flex column that overflows — a panel beside an
        // editor — a shrinkable item is squashed below it.
        'shrink-0 cursor-text gap-2 focus-within:border-primary/40',
        active && FILTER_CHIP_ACTIVE,
        width,
      )}
    >
      {leading}
      <input
        {...draft}
        type={type}
        inputMode={type === 'number' ? 'numeric' : undefined}
        placeholder={placeholder}
        className={cn(
          'min-w-0 bg-transparent text-xs outline-none placeholder:text-faint',
          // A figure gets a fixed column, text the rest of the chip.
          type === 'number' ? 'w-12 tabular-nums' : 'flex-1',
        )}
      />
    </label>
  )
}
